// Thin LOCAL fetch client for the patient doctor roster + rich profile + favorites (THU-358).
// Same data as the published PatientClient.listDoctors(), but it preserves the rich fields the
// published DoctorSummary schema strips on parse. Local-first, mirrors conversationsApi: baseUrl +
// getToken → Bearer, credentials:"include" (dev cookie), { data } unwrap. Swap back to the SDK once it
// carries these fields.
import { useMemo } from "react";
import { API_ORIGIN } from "../config";
import { useAppAuth } from "../auth";

// Full public doctor profile (the card). Every field is doctor-set in profile settings — no
// fabricated metrics. languages/focus_areas always arrive as arrays.
export interface DoctorProfile {
  id: string;
  first_name: string;
  last_name: string;
  specialty: string | null;
  avatar_url: string | null;
  bio: string | null;
  years_experience: number | null;
  education: string | null;
  languages: string[];
  focus_areas: string[];
  is_favorite: boolean;
}

/** Back-compat alias — the roster rows are full profiles now (still carry avatar_url). */
export type DoctorWithAvatar = DoctorProfile;

function shape(
  x: Partial<DoctorProfile> & { id: string; first_name: string; last_name: string },
): DoctorProfile {
  return {
    id: x.id,
    first_name: x.first_name,
    last_name: x.last_name,
    specialty: x.specialty ?? null,
    avatar_url: x.avatar_url ?? null,
    bio: x.bio ?? null,
    years_experience: x.years_experience ?? null,
    education: x.education ?? null,
    languages: x.languages ?? [],
    focus_areas: x.focus_areas ?? [],
    is_favorite: Boolean(x.is_favorite),
  };
}

export interface DoctorsApi {
  list(): Promise<DoctorProfile[]>;
  get(id: string): Promise<DoctorProfile>;
  favorite(id: string): Promise<void>;
  unfavorite(id: string): Promise<void>;
}

export function useDoctorsApi(): DoctorsApi {
  const { getToken } = useAppAuth();
  return useMemo<DoctorsApi>(() => {
    async function request<T>(method: string, path: string): Promise<T> {
      const headers: Record<string, string> = {};
      const token = await getToken();
      if (token) headers["Authorization"] = `Bearer ${token}`;
      const res = await fetch(`${API_ORIGIN}${path}`, { method, headers, credentials: "include" });
      const text = await res.text();
      const json: unknown = text ? JSON.parse(text) : {};
      if (!res.ok) {
        const err = (json as { error?: { message?: string } }).error;
        throw new Error(err?.message ?? "Request failed");
      }
      return (json as { data?: T }).data as T;
    }
    return {
      list: () =>
        request<DoctorProfile[]>("GET", "/v1/patient/doctors").then((d) => (d ?? []).map(shape)),
      get: (id) =>
        request<DoctorProfile>("GET", `/v1/patient/doctors/${encodeURIComponent(id)}`).then(shape),
      favorite: (id) =>
        request<unknown>("PUT", `/v1/patient/doctors/${encodeURIComponent(id)}/favorite`).then(
          () => undefined,
        ),
      unfavorite: (id) =>
        request<unknown>("DELETE", `/v1/patient/doctors/${encodeURIComponent(id)}/favorite`).then(
          () => undefined,
        ),
    };
  }, [getToken]);
}
